import React, { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';

import type { ChannelEnum } from 'js/src/client';
import { ApiService } from 'js/src/client';
import { BookProductCallCta } from 'js/src/components/common/BookProductCallCta';
import { Toast } from 'js/src/components/common/Toast';
import { PrimaryButton, SecondaryButton } from 'js/src/components/common/ui/Button';
import Modal from 'js/src/components/common/ui/modal';
import type { TypeInputChangeEvent } from 'js/src/components/common/ui/TextInput';
import TextInput, { TEXT_INPUT_VARIANTS } from 'js/src/components/common/ui/TextInput';
import { hideModal, isSubmitFeedbackModalShowing } from 'js/src/data/modals';
import { useAppDispatch } from 'js/src/libs/RootState';

interface SubmitFeedbackModalProps {
    channel?: ChannelEnum;
}

export const SubmitFeedbackModal: React.FC<SubmitFeedbackModalProps> = ({ channel }) => {
    const isShowing = useSelector(isSubmitFeedbackModalShowing);
    const dispatch = useAppDispatch();
    const [feedbackText, setFeedbackText] = useState('');
    const onChangeFeedbackText = (e: TypeInputChangeEvent) => setFeedbackText(e.target.value);

    const onClose = useCallback(() => {
        setFeedbackText('');
        dispatch(hideModal());
    }, [dispatch]);

    const onSubmit = useCallback(() => {
        try {
            ApiService.submitFeedbackCreate({
                body: {
                    page: window.location.pathname,
                    feedback: feedbackText,
                    ...(channel && { channel }),
                },
            });
        } catch (e) {
            // eslint-disable-next-line no-console
            console.error('Failed to submit feedback: ', e);
        }
        Toast.success('Thanks for your feedback!');
        onClose();
    }, [feedbackText, channel, onClose]);

    return (
        <Modal showing={isShowing} title="Submit feedback" onHidden={onClose}>
            <div>
                <form onSubmit={onSubmit}>
                    <TextInput
                        labelText={<div className="fw-semibold">Add your feedback</div>}
                        lines={5}
                        placeholder="Your feedback..."
                        text={feedbackText}
                        variant={TEXT_INPUT_VARIANTS.TEXTAREA}
                        onChange={onChangeFeedbackText}
                    />
                </form>
                <BookProductCallCta
                    className="mt-l5-12"
                    surface="submit_feedback_modal"
                    onBook={onClose}
                />
            </div>
            <div className="modal-button-footer">
                <SecondaryButton text="Cancel" onClick={onClose} />
                <PrimaryButton text="Submit" disabled={!feedbackText} onClick={onSubmit} />
            </div>
        </Modal>
    );
};
